import { z } from 'zod';
import type { SectionId } from './reportEnums.constants';

/** Section ids whose body copy is CSM-authored and stored on `dataSnapshot`. */
export const CSM_AUTHORED_SECTION_IDS: readonly SectionId[] = [
  'member-spotlight',
];

/** Default eyebrow / heading labels shown in Member Spotlight. */
export const MEMBER_SPOTLIGHT_LABELS = {
  needs: 'Needs',
  ongoingPriorities: 'Ongoing Priorities',
  summary: 'Summary',
  challenge: 'The Challenge',
  howReemaHelped: 'How Reema Made the Difference',
  outcomes: 'Outcomes',
} as const;

/** Bullet-list keys on a Member Spotlight snapshot. */
export type MemberSpotlightListKey =
  | 'needs'
  | 'ongoingPriorities'
  | 'challenge'
  | 'howReemaHelped'
  | 'outcomes';

const MemberSpotlightLabelsSchema = z.object({
  needs: z.string(),
  ongoingPriorities: z.string(),
  summary: z.string(),
  challenge: z.string(),
  howReemaHelped: z.string(),
  outcomes: z.string(),
});

/** Zod schema for the CSM-authored Member Spotlight snapshot. */
export const MemberSpotlightSnapshotSchema = z.object({
  name: z.string(),
  summary: z.string(),
  needs: z.array(z.string()),
  ongoingPriorities: z.array(z.string()),
  challenge: z.array(z.string()),
  howReemaHelped: z.array(z.string()),
  outcomes: z.array(z.string()),
  labels: MemberSpotlightLabelsSchema,
});

/** Parsed Member Spotlight snapshot stored on `dataSnapshot`. */
export type MemberSpotlightSnapshot = z.infer<
  typeof MemberSpotlightSnapshotSchema
>;

/**
 * Returns whether a section stores CSM-authored copy on `dataSnapshot`.
 * @param sectionId - Library section id.
 * @returns True for Member Spotlight (and future CSM-authored sections).
 */
export const isCsmAuthoredSection = (sectionId: SectionId): boolean =>
  CSM_AUTHORED_SECTION_IDS.includes(sectionId);

/**
 * Default Jessica template used for draft preview and first persist.
 * @returns Complete Member Spotlight snapshot.
 */
export const defaultMemberSpotlightSnapshot = (): MemberSpotlightSnapshot => ({
  name: 'Jessica',
  summary:
    'Jessica | 29-year-old domestic violence survivor and single parent navigating housing instability, financial strain, and divorce',
  needs: [
    'Behavioral health support',
    'Legal aid coordination',
    'Housing assistance',
    'Financial stability support',
  ],
  ongoingPriorities: [
    'Continue trauma-focused telehealth therapy',
    'Maintain progress toward housing and financial stability',
  ],
  challenge: [
    'Recent domestic violence survivor suddenly navigating single parenthood',
    'Facing profound housing instability and significant financial strain',
    'Overwhelming legal process of divorce compounding acute stress',
    'Managing pre-existing depression and ADHD while working multiple jobs',
  ],
  howReemaHelped: [
    'Community Guide Joel built trust by helping update her medical ID after her name change',
    'Researched legal aid resources to support her divorce proceedings',
    'Sent housing assistance information to address her housing crisis',
    'Helped her find and apply for higher-paying jobs to stabilize finances',
  ],
  outcomes: [
    "Accepted referral to Reema's telehealth therapy program for trauma-focused support",
    'Successfully connected with legal aid to manage her divorce',
    'Achieved greater life stability across housing, finances, and legal issues',
    'Engaging more consistently in mental health care and building toward independence',
  ],
  labels: { ...MEMBER_SPOTLIGHT_LABELS },
});

const readStoredString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

const readStoredStringArray = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }
  return value.filter((item): item is string => typeof item === 'string');
};

/**
 * Merges stored snapshot JSON over Jessica defaults.
 * Missing fields use defaults. Present strings (including empty) and arrays
 * (including empty) are kept so CSM clears are not overwritten.
 * @param data - Stored `dataSnapshot` JSON or preview mock object.
 * @returns Complete Member Spotlight snapshot.
 */
export const parseMemberSpotlightSnapshot = (
  data: Record<string, unknown> | null | undefined,
): MemberSpotlightSnapshot => {
  const defaults = defaultMemberSpotlightSnapshot();
  if (!data || typeof data !== 'object') {
    return defaults;
  }

  const storedLabels =
    data.labels && typeof data.labels === 'object' && !Array.isArray(data.labels)
      ? (data.labels as Record<string, unknown>)
      : undefined;

  return {
    name: readStoredString(data.name) ?? defaults.name,
    summary: readStoredString(data.summary) ?? defaults.summary,
    needs: readStoredStringArray(data.needs) ?? defaults.needs,
    ongoingPriorities:
      readStoredStringArray(data.ongoingPriorities) ??
      defaults.ongoingPriorities,
    challenge: readStoredStringArray(data.challenge) ?? defaults.challenge,
    howReemaHelped:
      readStoredStringArray(data.howReemaHelped) ?? defaults.howReemaHelped,
    outcomes: readStoredStringArray(data.outcomes) ?? defaults.outcomes,
    labels: {
      needs:
        readStoredString(storedLabels?.needs) ?? defaults.labels.needs,
      ongoingPriorities:
        readStoredString(storedLabels?.ongoingPriorities) ??
        defaults.labels.ongoingPriorities,
      summary:
        readStoredString(storedLabels?.summary) ?? defaults.labels.summary,
      challenge:
        readStoredString(storedLabels?.challenge) ?? defaults.labels.challenge,
      howReemaHelped:
        readStoredString(storedLabels?.howReemaHelped) ??
        defaults.labels.howReemaHelped,
      outcomes:
        readStoredString(storedLabels?.outcomes) ?? defaults.labels.outcomes,
    },
  };
};

/**
 * Serializes a parsed snapshot for `dataSnapshot` PATCH / local state.
 * @param snapshot - Complete Member Spotlight snapshot.
 * @returns JSON-safe record.
 */
export const memberSpotlightSnapshotToRecord = (
  snapshot: MemberSpotlightSnapshot,
): Record<string, unknown> => ({
  name: snapshot.name,
  summary: snapshot.summary,
  needs: snapshot.needs,
  ongoingPriorities: snapshot.ongoingPriorities,
  challenge: snapshot.challenge,
  howReemaHelped: snapshot.howReemaHelped,
  outcomes: snapshot.outcomes,
  labels: { ...snapshot.labels },
});

/**
 * Snapshot JSON to store on a template for a CSM-authored section.
 * Warehouse section snapshots are not copied.
 * @param sectionId - Library section id.
 * @param dataSnapshot - Report section snapshot.
 * @returns Parsed spotlight record, or undefined when the section is not
 *   CSM-authored or has no stored snapshot.
 */
export const csmAuthoredSnapshotForTemplate = (
  sectionId: SectionId,
  dataSnapshot: Record<string, unknown> | null | undefined,
): Record<string, unknown> | undefined => {
  if (!isCsmAuthoredSection(sectionId) || !dataSnapshot) {
    return undefined;
  }
  return memberSpotlightSnapshotToRecord(
    parseMemberSpotlightSnapshot(dataSnapshot),
  );
};

/**
 * Replaces one bullet in a Member Spotlight list.
 * @param snapshot - Current snapshot.
 * @param key - List field to update.
 * @param index - Item index.
 * @param value - Replacement text.
 * @returns New snapshot with the item replaced. Unchanged when the index is out of range.
 */
export const setMemberSpotlightListItem = (
  snapshot: MemberSpotlightSnapshot,
  key: MemberSpotlightListKey,
  index: number,
  value: string,
): MemberSpotlightSnapshot => {
  const items = snapshot[key];
  if (index < 0 || index >= items.length) {
    return snapshot;
  }
  const next = [...items];
  next[index] = value;
  return { ...snapshot, [key]: next };
};

/**
 * Appends an empty bullet to a Member Spotlight list.
 * @param snapshot - Current snapshot.
 * @param key - List field to update.
 * @returns New snapshot with one extra empty item.
 */
export const appendMemberSpotlightListItem = (
  snapshot: MemberSpotlightSnapshot,
  key: MemberSpotlightListKey,
): MemberSpotlightSnapshot => ({
  ...snapshot,
  [key]: [...snapshot[key], ''],
});

/**
 * Removes a bullet from a Member Spotlight list, keeping at least one item.
 * @param snapshot - Current snapshot.
 * @param key - List field to update.
 * @param index - Item index to remove.
 * @returns New snapshot. Unchanged when the list has one item or the index is invalid.
 */
export const removeMemberSpotlightListItem = (
  snapshot: MemberSpotlightSnapshot,
  key: MemberSpotlightListKey,
  index: number,
): MemberSpotlightSnapshot => {
  const items = snapshot[key];
  if (items.length <= 1 || index < 0 || index >= items.length) {
    return snapshot;
  }
  return {
    ...snapshot,
    [key]: items.filter((_, itemIndex) => itemIndex !== index),
  };
};
